import React from 'react';
import assistantChanges from './assistant.changes.md';
import assistantClient from '@requests/telnyx/assistants.requests';

import { Stack } from '@mui/material';
import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useNodeError } from '@common/hooks/useNodeError';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import { NodeAnchors, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { AssistantNodeState, NodeConfig, NodeField, NodeFieldType, NodeInputType, NodeOutput, NodeType } from '../..';
import { AssistantNodeChips } from './AssistantNodeChips';
import { useQuery } from '@tanstack/react-query';
import { ProviderModel } from '../AssistantRouter/ProviderModel';

const exampleFiles = [
  { type: 'image_url', image_url: { url: 'data:image/png;base64,...' } },
  { type: 'text', text: 'CSV file content' },
  { type: 'file', file: { filename: 'example.pdf', file_data: 'data:application/pdf;base64...' } },
];

export const config: NodeConfig = {
  version: '1.0',
  title: 'AI Assistant',
  identifier: 'Assistant',
  command_id: 'assistant.single',
  background: '#3434EF',
  icon: 'AI',
  description: 'Execute a completion using an AI Assistant.',
  changelog: assistantChanges,
  anchors: [
    {
      label: 'Execute',
      id: 'assistant.single.call',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'assistant.single.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      label: 'Finished',
      id: 'assistant.single.finished',
      type: 'source' as HandleType,
      variant: 'execution',
    },
    {
      label: 'Data',
      id: 'assistant.single.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
};

export const state: AssistantNodeState = {
  name: '',
  session_id: { type: NodeInputType.Expression, value: '' },
  include_session_tools: 'true',
  query: { type: NodeInputType.Expression, value: '' },
  files: { type: NodeInputType.Expression, value: '' },
  assistant_id: '',
  sender_id: { type: NodeInputType.Expression, value: '' },
};

export const fields: NodeField[] = [
  {
    id: 'name',
    tab: '1',
    type: NodeFieldType.Input,
    default_type: NodeInputType.Text,
    label: 'Name',
    display_name: true,
    description: 'Use this field to give your node a distinct name.',
    placeholder: 'Give this node a distinct name',
    required: true,
  },
  {
    id: 'session_id',
    tab: 'node_extra',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Session ID',
    description:
      'Specify a unique identifier to enable chat history. Multiple request with the same session ID will be able to read the previous conversation history.',
    placeholder: '1234-1234-1234-1234',
  },
  {
    id: 'include_session_tools',
    tab: '1',
    type: NodeFieldType.Toggle,
    label: 'Include Session Tools',
    description:
      'When enabled and using a session ID, the previous tool calls and results will be included in the conversation history.',
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
  },
  {
    id: 'sender_id',
    tab: 'node_extra',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Sender ID',
    description:
      'Optionally specify an ID to identify the user sending the request. The ID will be present in the request reporting. Useful for multi-user applications.',
    placeholder: '1234-1234-1234-1234',
  },
  {
    id: 'query',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Query',
    description: 'This is the query that will be passed to the router on behalf of the customer.',
    placeholder: 'The users query',
  },
  {
    id: 'system',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'System Message',
    description: 'Define instructions for the router to use when writing an answer.',
    placeholder: 'Write a system message',
  },
  {
    id: 'assistant_id',
    tab: '1',
    type: NodeFieldType.AssistantRouter,
    label: 'Assistant',
    description: 'Select an assistant to be used to answer the input query.',
    placeholder: 'Select an assistant',
  },
  {
    id: 'files',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Files',
    description: `An array of files using the message array file structure. Use a dynamic variable to pass an array of files.\n\n${JSON.stringify(
      exampleFiles,
      null,
      2
    )}`,
    placeholder: 'A dynamic variable containing an array of files',
  },
];

export const output: NodeOutput = {
  default: {
    assistant: {
      single: {
        query: 'query',
        answer: 'answer',
        message_id: 'message_id',
        session_id: null,
        model: 'gpt-4-32k',
        system: 'system_message',
        prompt_tokens: 18,
        completion_tokens: 9,
        metadata: {
          tool_completions: [],
          documents: [],
          events: {},
        },
      },
    },
  },
  dynamic: null,
};

export const Assistant: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background: _background, anchors, icon } = config;

  const themeType = useSelector((state: State) => state.flow.theme);
  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  const { data: assistants = [] } = useQuery({
    queryKey: ['ai_assistants'],
    queryFn: assistantClient.list,
    retry: false,
  });

  if (!command) return null;
  if (command.config.command_id !== 'assistant.single') return null;

  const nodeState: AssistantNodeState = command.state;

  const displayFieldId = command.fields.find((field) => field.display_name)?.id;
  const displayTitle = displayFieldId ? (nodeState[displayFieldId as keyof AssistantNodeState] as string) : null;

  const background = themeType === 'dark' ? '#5858FF' : _background;
  const isToolbarVisible = Boolean(isNodeError);
  const assistant = assistants.find((assistant) => assistant.id === nodeState.assistant_id);
  const model = assistant?.model;

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={displayTitle || title} icon={icon} background={background} status={nodeStatus} />
      {model && <ProviderModel model={model} />}
      <AssistantNodeChips assistantIds={[nodeState.assistant_id]} system={assistant?.instructions} />
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
